import * as XLSX from 'xlsx';

/**
 * Generates an Excel file with bioreactor data for all completed runs
 * @param {Object} project - The project data containing configuration
 * @param {Array} savedRuns - Array of completed bioreactor runs
 * @returns {void} - Downloads the Excel file
 */
export const generateCompleteRunsExcel = (project, savedRuns) => {
  try {
    console.log('Excel Generator - Starting generation with:');
    console.log('Project:', project?.projectName);
    console.log('Number of runs:', savedRuns?.length);
    console.log('Run IDs:', savedRuns?.map(r => r.runId));
    console.log('Excel Generator - Full project object:', project);
    console.log('Excel Generator - Custom parameters debug:');
    console.log('  process_custom:', project?.process_custom);
    console.log('  quality_custom:', project?.quality_custom);
    console.log('  bioreactor_custom:', project?.bioreactor_custom);
    
    // Define base columns
    const baseColumns = ['Run Id', 'Day', 'Sample Date', 'Sample Time'];
      // Get process parameters from project configuration
    const processParameters = [];
    
    // Handle new selectedProcessParameters structure (dynamic parameters from backend)
    if (project.selectedProcessParameters && typeof project.selectedProcessParameters === 'object') {
      console.log('Excel Generator - Processing new selectedProcessParameters:', project.selectedProcessParameters);
      Object.entries(project.selectedProcessParameters).forEach(([paramName, isSelected]) => {
        if (isSelected) {
          processParameters.push(paramName);
        }
      });
    }

    // Handle legacy process parameters for backward compatibility
    const processParamMap = {
      process_temp: 'Temperature',
      process_pressure: 'Pressure', 
      process_agitation: 'Agitation Speed',
      process_ph: 'pH',
      process_do: 'Dissolved Oxygen',
      process_co2: 'CO2 Level',
      process_feedrate: 'Feed Rate',
      process_feedcomp: 'Feed Composition'
    };

    // Add selected process parameters (legacy)
    Object.keys(processParamMap).forEach(key => {
      if (project[key]) {
        processParameters.push(processParamMap[key]);
      }
    });

    // Add custom process parameters (legacy)
    if (project.process_custom && project.process_custom.length > 0) {
      console.log('Excel Generator - Processing process_custom:', project.process_custom);
      project.process_custom.forEach(param => {
        console.log('Excel Generator - Processing custom process param:', param, 'value:', param.value);
        if (param.value) {
          processParameters.push(param.name);
          console.log('Excel Generator - Added custom process parameter:', param.name);
        } else {
          console.log('Excel Generator - Skipped custom process parameter (value falsy):', param.name, param.value);
        }
      });
    } else {
      console.log('Excel Generator - No process_custom found or empty array');
    }

    // Get quality attributes from project configuration
    const qualityAttributes = [];
    
    // Handle new selectedQualityAttributes structure (dynamic attributes from backend)
    if (project.selectedQualityAttributes && typeof project.selectedQualityAttributes === 'object') {
      console.log('Excel Generator - Processing new selectedQualityAttributes:', project.selectedQualityAttributes);
      Object.entries(project.selectedQualityAttributes).forEach(([attrName, isSelected]) => {
        if (isSelected) {
          qualityAttributes.push(attrName);
        }
      });
    }

    // Handle legacy quality attributes for backward compatibility
    const qualityAttrMap = {
      quality_titer: 'Product Titer',
      quality_purity: 'Product Purity',
      quality_bioactivity: 'Biological Activity',
      quality_viability: 'Cell Viability',
      quality_density: 'Cell Density',
      quality_growth: 'Growth Rate',
      quality_consistency: 'Process Consistency',
      quality_contamination: 'Contamination Control'
    };

    // Add selected quality attributes (legacy)
    Object.keys(qualityAttrMap).forEach(key => {
      if (project[key]) {
        qualityAttributes.push(qualityAttrMap[key]);
      }
    });

    // Add custom quality attributes (legacy)
    if (project.quality_custom && project.quality_custom.length > 0) {
      console.log('Excel Generator - Processing quality_custom:', project.quality_custom);
      project.quality_custom.forEach(attr => {
        console.log('Excel Generator - Processing custom quality attr:', attr, 'value:', attr.value);
        if (attr.value) {
          qualityAttributes.push(attr.name);
          console.log('Excel Generator - Added custom quality attribute:', attr.name);
        } else {
          console.log('Excel Generator - Skipped custom quality attribute (value falsy):', attr.name, attr.value);
        }
      });
    } else {
      console.log('Excel Generator - No quality_custom found or empty array');
    }

    // Get bioreactor data parameters
    const bioreactorData = [];
    
    // Handle new selectedBioreactorData structure (dynamic data from backend)
    if (project.selectedBioreactorData && typeof project.selectedBioreactorData === 'object') {
      console.log('Excel Generator - Processing new selectedBioreactorData:', project.selectedBioreactorData);
      Object.entries(project.selectedBioreactorData).forEach(([dataName, isSelected]) => {
        if (isSelected) {
          bioreactorData.push(dataName);
        }
      });
    }

    // Handle legacy bioreactor configuration for backward compatibility
    const bioreactorParamMap = {
      reactor_str: 'Stirred Tank Reactor',
      reactor_air: 'Air-Lift Reactor',
      reactor_packed: 'Packed Bed Reactor',
      control_temp: 'Temperature Control',
      control_ph: 'pH Control',
      control_do: 'Dissolved Oxygen Control',
      monitor_online: 'Online Monitoring',
      monitor_logging: 'Data Logging'
    };

    // Add selected bioreactor parameters (legacy)
    Object.keys(bioreactorParamMap).forEach(key => {
      if (project[key]) {
        bioreactorData.push(bioreactorParamMap[key]);
      }
    });

    // Add custom bioreactor parameters (legacy)
    if (project.bioreactor_custom && project.bioreactor_custom.length > 0) {
      console.log('Excel Generator - Processing bioreactor_custom:', project.bioreactor_custom);
      project.bioreactor_custom.forEach(param => {
        console.log('Excel Generator - Processing custom bioreactor param:', param, 'value:', param.value);
        if (param.value) {
          bioreactorData.push(param.name);
          console.log('Excel Generator - Added custom bioreactor parameter:', param.name);
        } else {
          console.log('Excel Generator - Skipped custom bioreactor parameter (value falsy):', param.name, param.value);
        }
      });
    } else {
      console.log('Excel Generator - No bioreactor_custom found or empty array');
    }    // Combine columns - Include Process Parameters, Quality Attributes, AND Bioreactor Data
    const allColumns = [...baseColumns, ...processParameters, ...qualityAttributes, ...bioreactorData];

    console.log('Excel Generator - Final column arrays:');
    console.log('  processParameters:', processParameters);
    console.log('  qualityAttributes:', qualityAttributes);
    console.log('  bioreactorData:', bioreactorData);
    console.log('  allColumns:', allColumns);

    // Generate data rows
    const dataRows = [];
    
    // Add header row
    dataRows.push(allColumns);    // Generate rows for each run (Days 0-14 for each Run ID)
    console.log('Excel Generator - Processing runs for data rows...');
    savedRuns.forEach((run, index) => {
      console.log(`Processing run ${index + 1}:`, run.runId);
      const runStartDate = new Date(run.cultureStartDateTime);
      
      for (let day = 0; day <= 14; day++) {
        const sampleDate = new Date(runStartDate);
        sampleDate.setDate(runStartDate.getDate() + day);
        
        const row = [
          run.runId,
          day,
          sampleDate.toLocaleDateString('en-US'), // Sample Date
          sampleDate.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          }) // Sample Time
        ];        // Add empty cells for process parameters, quality attributes, AND bioreactor data
        // These will be filled manually by the user later
        for (let i = 0; i < processParameters.length + qualityAttributes.length + bioreactorData.length; i++) {
          row.push(''); // Empty cell for data entry
        }

        dataRows.push(row);
      }
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(dataRows);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 15 }, // Run Id
      { wch: 8 },  // Day
      { wch: 12 }, // Sample Date
      { wch: 12 }, // Sample Time
      ...processParameters.map(() => ({ wch: 15 })), // Process Parameters
      ...qualityAttributes.map(() => ({ wch: 15 })),  // Quality Attributes
      ...bioreactorData.map(() => ({ wch: 15 }))     // Bioreactor Data
    ];
    worksheet['!cols'] = columnWidths;

    // Style the header row
    const headerRange = XLSX.utils.decode_range(worksheet['!ref']);
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellAddress]) continue;
      
      worksheet[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
    }

    // Add borders to all data cells
    for (let row = 1; row <= headerRange.e.r; row++) {
      for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!worksheet[cellAddress]) {
          worksheet[cellAddress] = { v: "" }; // Create empty cell if it doesn't exist
        }
        
        worksheet[cellAddress].s = {
          border: {
            top: { style: "thin", color: { rgb: "D0D0D0" } },
            bottom: { style: "thin", color: { rgb: "D0D0D0" } },
            left: { style: "thin", color: { rgb: "D0D0D0" } },
            right: { style: "thin", color: { rgb: "D0D0D0" } }
          },
          alignment: { vertical: "center" }
        };
      }
    }

    // Highlight Run Id and Day columns with alternating colors
    for (let row = 1; row <= headerRange.e.r; row++) {
      const runIdCell = XLSX.utils.encode_cell({ r: row, c: 0 });
      const dayCell = XLSX.utils.encode_cell({ r: row, c: 1 });
      
      if (worksheet[runIdCell]) {
        worksheet[runIdCell].s = {
          ...worksheet[runIdCell].s,
          fill: { fgColor: { rgb: "E7F3FF" } }
        };
      }
      
      if (worksheet[dayCell]) {
        worksheet[dayCell].s = {
          ...worksheet[dayCell].s,
          fill: { fgColor: { rgb: "F0F8FF" } }
        };
      }
    }

    // Add the worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Complete Runs Data');

    // Generate filename
    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = `${project.projectName}_Complete_Runs_${timestamp}.xlsx`;    // Generate the Excel file as binary data for viewing (no download)
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    console.log('Excel Generator - Generation completed successfully!');
    console.log(`File: ${filename}`);
    console.log(`Total data rows: ${dataRows.length - 1} (excluding header)`);
    console.log(`Expected rows: ${savedRuns.length * 15} (Days 0-14 for each run)`);

    return {
      success: true,
      filename,
      excelData: excelBuffer,
      message: `Excel file is created successfully!`
    };

  } catch (error) {
    console.error('Error generating Excel file:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to generate Excel file. Please try again.'
    };
  }
};

/**
 * Generates a sample Excel template based on project configuration
 * @param {Object} project - The project data containing configuration
 * @returns {void} - Downloads the template Excel file
 */
export const generateExcelTemplate = (project) => {
  try {
    // This is a simplified version for generating just the template
    const result = generateCompleteRunsExcel(project, [
      {
        runId: 'SAMPLE-RUN-001',
        cultureStartDateTime: new Date().toISOString()
      }
    ]);
    
    return result;
  } catch (error) {
    console.error('Error generating Excel template:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to generate Excel template. Please try again.'
    };
  }
};
